// client/src/components/admin/RequestsManagement.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const RequestsManagement = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const res = await axios.get('/api/requests');
        
        // Ensure we have a valid array of requests
        const receivedRequests = res.data.requests || [];
        
        setRequests(receivedRequests);
        setFilteredRequests(receivedRequests);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching requests:', err);
        setError('Failed to fetch requests');
        setLoading(false);
      }
    };
    
    fetchRequests();
  }, []);
  
  useEffect(() => {
    // Filter requests based on active tab
    if (activeTab === 'all') {
      setFilteredRequests(requests);
    } else {
      setFilteredRequests(requests.filter(request => request.status === activeTab));
    }
  }, [activeTab, requests]);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  // Helper function to format currency with currency code
  const formatCurrency = (amount, currency = 'EUR') => {
    if (amount === undefined || amount === null) return `${currency} 0`;
    return `${currency} ${Number(amount).toLocaleString('en-US')}`;
  };
  
  // Helper function to safely get company name
  const getCompanyName = (company) => {
    if (!company) return 'Deleted Company';
    return typeof company === 'object' && company.name ? company.name : 'Company ID: ' + company;
  };
  
  if (loading) {
    return <div className="loading">Loading requests...</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  return (
    <div className="admin-container">
      <h2 className="admin-page-title">Liquidity Requests</h2>
      
      <div className="admin-tab-container">
        <div className="admin-tabs">
          <div 
            className={`admin-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => handleTabChange('all')}
          >
            All Requests
          </div>
          <div 
            className={`admin-tab ${activeTab === 'bidding' ? 'active' : ''}`}
            onClick={() => handleTabChange('bidding')}
          >
            Bidding
          </div>
          <div 
            className={`admin-tab ${activeTab === 'pending_allocation' ? 'active' : ''}`}
            onClick={() => handleTabChange('pending_allocation')}
          >
            Pending Allocation
          </div>
          <div 
            className={`admin-tab ${activeTab === 'completed' ? 'active' : ''}`}
            onClick={() => handleTabChange('completed')}
          >
            Completed
          </div>
          <div 
            className={`admin-tab ${activeTab === 'cancelled' ? 'active' : ''}`}
            onClick={() => handleTabChange('cancelled')}
          >
            Cancelled
          </div>
        </div>
      </div>
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Liquidity Allocation Requests</h3>
        </div>
        <div className="admin-card-body">
          <div className="admin-table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>Company</th>
                  <th>Amount</th>
                  <th>Bids Received</th>
                  <th>Date Created</th>
                  <th>Status</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.length > 0 ? (
                  filteredRequests.map(request => {
                    // Safely access properties with null checks
                    const requestId = request.requestId || 'N/A';
                    // Handle null or undefined company
                    const companyName = getCompanyName(request.company);
                    const amount = request.amount || 0;
                    const currency = request.currency || 'EUR';
                    const totalBidAmount = request.totalBidAmount || 0;
                    const createdAt = request.createdAt ? new Date(request.createdAt).toLocaleDateString() : 'N/A';
                    const status = request.status || 'unknown';
                    const id = request.id || request._id;
                    
                    return (
                      <tr key={id || `req-${requestId}`}>
                        <td>#{requestId}</td>
                        <td>{companyName}</td>
                        <td>{formatCurrency(amount, currency)}</td>
                        <td>{formatCurrency(totalBidAmount, currency)}</td>
                        <td>{createdAt}</td>
                        <td>
                          <span className={`status status-${status}`}>
                            {status.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          <Link
                            to={`/admin/requests/${id}`}
                            className="admin-view-btn"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7">No requests found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestsManagement;